import React, { useState, useEffect } from 'react';
import { MessageCircle, ThumbsUp, Heart, Search, Smile, Pencil, Trash2, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const REACTION_EMOJIS = ["❤️", "👍", "😂", "🔥", "🎉", "👏", "💯", "✨"];

const ForumTab = () => {
  // State
  const [forumThreads, setForumThreads] = useState([]);
  const [forumCategories, setForumCategories] = useState([]);
  const [loadingForum, setLoadingForum] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadComments, setThreadComments] = useState([]);
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const [newThreadContent, setNewThreadContent] = useState('');
  const [newThreadCategory, setNewThreadCategory] = useState('');
  const [commentContent, setCommentContent] = useState('');
  const [forumSearchTerm, setForumSearchTerm] = useState('');
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [reactionPickerThreadId, setReactionPickerThreadId] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [reactionPickerCommentId, setReactionPickerCommentId] = useState(null);
  const [detailViewMode, setDetailViewMode] = useState('post'); // 'post' or 'comments'

  // Load forum categories
  const loadForumCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/categories`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[FORUM] Raw categories from API:', data);
        
        // Whitelist of valid forum category IDs (to prevent news source pollution)
        const validForumIds = ['qa', 'gear-equipment', 'mixing-techniques', 'gig-stories', 
                               'venues-bookings', 'tips-tricks'];
        
        // Filter to ensure ONLY valid forum categories
        const validCategories = (data || []).filter(cat => 
          cat.description && // Must have description (news sources don't)
          cat.id && 
          cat.name &&
          validForumIds.includes(cat.id) // ID must be in whitelist
        );
        console.log('[FORUM] Filtered valid categories:', validCategories);
        
        setForumCategories(validCategories);
      }
    } catch (error) {
      console.error('[FORUM] Failed to load forum categories:', error);
    }
  };

  // Load forum threads
  const loadForumThreads = async () => {
    setLoadingForum(true);
    try {
      const categoryParam = selectedCategory !== 'all' ? `category=${encodeURIComponent(selectedCategory)}&` : '';
      const response = await fetch(`${BACKEND_URL}/api/forum/posts?${categoryParam}limit=50`);
      
      if (response.ok) {
        const data = await response.json();
        setForumThreads(data);
      }
    } catch (error) {
      console.error('Failed to load forum threads:', error);
    } finally {
      setLoadingForum(false);
    }
  };

  // Load thread comments
  const loadThreadComments = async (threadId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${threadId}/replies`);
      
      if (response.ok) {
        const data = await response.json();
        setThreadComments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  // Create thread
  const createThread = async () => {
    if (!newThreadTitle || !newThreadContent || !newThreadCategory) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: newThreadTitle,
          content: newThreadContent,
          category: newThreadCategory
        })
      });

      if (response.ok) {
        setShowCreateThread(false);
        setNewThreadTitle('');
        setNewThreadContent('');
        setNewThreadCategory('');
        loadForumThreads();
      } else {
        alert('Failed to create thread');
      }
    } catch (error) {
      console.error('Failed to create thread:', error);
      alert('Failed to create thread');
    }
  };

  // Post comment
  const postComment = async () => {
    if (!commentContent) {
      return;
    }

    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedThread.id}/replies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: commentContent
        })
      });

      if (response.ok) {
        setCommentContent('');
        loadThreadComments(selectedThread.id);
        loadForumThreads();
      }
    } catch (error) {
      console.error('Failed to post comment:', error);
    }
  };

  // Handle love thread
  const handleLoveThread = async (threadId, event) => {
    event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to love threads');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${threadId}/react`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reaction_type: 'love'
        })
      });

      if (response.ok) {
        loadForumThreads();
      }
    } catch (error) {
      console.error('Failed to love thread:', error);
    }
  };

  // Handle like thread
  const handleLikeThread = async (threadId, event) => {
    event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to like threads');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${threadId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        loadForumThreads();
      }
    } catch (error) {
      console.error('Failed to like thread:', error);
    }
  };

  // Handle delete thread
  const handleDeleteThread = async (threadId, event) => {
    if (event) event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to delete threads');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this thread? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${threadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSelectedThread(null);
        setThreadComments([]);
        loadForumThreads();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete thread');
      }
    } catch (error) {
      console.error('Failed to delete thread:', error);
      alert('Failed to delete thread');
    }
  };

  // Handle edit thread
  const handleEditThread = async (threadId, event) => {
    if (event) event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;

    try {
      const thread = forumThreads.find(t => t.id === threadId) || selectedThread;
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${threadId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: editTitle,
          content: editContent,
          category: thread?.category || 'qa'
        })
      });

      if (response.ok) {
        setEditingThreadId(null);
        setEditTitle('');
        setEditContent('');
        loadForumThreads();
        if (selectedThread && selectedThread.id === threadId) {
          const updated = await response.json();
          setSelectedThread(updated);
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to edit thread');
      }
    } catch (error) {
      console.error('Failed to edit thread:', error);
      alert('Failed to edit thread');
    }
  };

  // Start editing
  const startEditing = (thread, event) => {
    if (event) event.stopPropagation();
    setEditingThreadId(thread.id);
    setEditTitle(thread.title);
    setEditContent(thread.content);
  };

  // Cancel editing
  const cancelEditing = (event) => {
    if (event) event.stopPropagation();
    setEditingThreadId(null);
    setEditTitle('');
    setEditContent('');
  };

  // Handle reaction
  const handleReaction = async (threadId, emoji, event) => {
    if (event) event.stopPropagation();
    
    const token = localStorage.getItem('beatseek_token');
    if (!token) {
      alert('Please login to react');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${threadId}/reactions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ emoji })
      });

      if (response.ok) {
        setReactionPickerThreadId(null);
        loadForumThreads();
      }
    } catch (error) {
      console.error('Failed to react:', error);
    }
  };

  // Delete comment
  const handleDeleteComment = async (commentId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedThread.id}/replies/${commentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        loadThreadComments(selectedThread.id);
        loadForumThreads();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete comment');
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  // Edit comment
  const handleEditComment = async (commentId) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token || !editCommentContent.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${selectedThread.id}/replies/${commentId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: editCommentContent })
      });
      if (response.ok) {
        setEditingCommentId(null);
        setEditCommentContent('');
        loadThreadComments(selectedThread.id);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to edit comment');
      }
    } catch (error) {
      console.error('Failed to edit comment:', error);
    }
  };

  // React to comment
  const handleCommentReaction = async (commentId, emoji) => {
    const token = localStorage.getItem('beatseek_token');
    if (!token) { alert('Please login to react'); return; }

    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/replies/${commentId}/reactions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji })
      });
      if (response.ok) {
        setReactionPickerCommentId(null);
        loadThreadComments(selectedThread.id);
      }
    } catch (error) {
      console.error('Failed to react to comment:', error);
    }
  };
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Helper: Format number
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num;
  };

  // Load on mount and when category changes
  useEffect(() => {
    loadForumThreads();
    loadForumCategories();
  }, [selectedCategory]);

  // Thread Detail View
  if (selectedThread) {
    return (
      <div style={{ padding: '1rem', minHeight: '100%', height: 'calc(100vh - 220px)', maxHeight: '75vh', overflowY: 'auto' }}>
        {/* Back button and Delete button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <button
            onClick={() => {setSelectedThread(null); setThreadComments([]); setCommentContent(''); setDetailViewMode('post');}}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#ff00ff',
              padding: '0.5rem 0',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#00ffff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#ff00ff';
            }}
          >
            ← Back to Threads
          </button>

          {/* Delete button - only show if user is the author */}
          {(() => {
            const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
            const isAuthor = currentUser.id === selectedThread.author_id || currentUser.user_id === selectedThread.author_id;
            return isAuthor ? (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => {
                    setEditingThreadId(selectedThread.id);
                    setEditTitle(selectedThread.title);
                    setEditContent(selectedThread.content);
                  }}
                  style={{
                    background: 'rgba(0, 255, 255, 0.1)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    color: '#00ffff',
                    padding: '0.5rem 1rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Pencil size={14} /> Edit
                </button>
                <button
                  onClick={() => handleDeleteThread(selectedThread.id)}
                  style={{
                    background: 'rgba(255, 0, 0, 0.1)',
                    border: '1px solid rgba(255, 0, 0, 0.3)',
                    color: '#ff4444',
                    padding: '0.5rem 1rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            ) : null;
          })()}
        </div>

        {/* Mode toggle tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <button
            data-testid="mode-view-post"
            onClick={() => setDetailViewMode('post')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: detailViewMode === 'post' ? '1px solid rgba(255, 0, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
              background: detailViewMode === 'post' ? 'rgba(255, 0, 255, 0.15)' : 'transparent',
              color: detailViewMode === 'post' ? '#ff00ff' : 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            View Post
          </button>
          <button
            data-testid="mode-view-comments"
            onClick={() => setDetailViewMode('comments')}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              border: detailViewMode === 'comments' ? '1px solid rgba(0, 255, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
              background: detailViewMode === 'comments' ? 'rgba(0, 255, 255, 0.15)' : 'transparent',
              color: detailViewMode === 'comments' ? '#00ffff' : 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Comments ({threadComments.length})
          </button>
        </div>

        {detailViewMode === 'post' && (
        <>
        {/* Thread content */}
        <div style={{
          background: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '0.75rem',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{
              background: 'rgba(255, 0, 255, 0.2)',
              border: '1px solid rgba(255, 0, 255, 0.5)',
              color: '#ff00ff',
              fontSize: '0.75rem',
              padding: '0.25rem 0.75rem',
              borderRadius: '0.75rem',
              fontWeight: '600'
            }}>
              {forumCategories.find(c => c.id === selectedThread.category)?.name || selectedThread.category}
            </div>
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
              by {selectedThread.author_name} • {getTimeAgo(selectedThread.created_at)}
            </span>
          </div>

          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fff', marginBottom: '1rem' }}>
            {selectedThread.title}
          </h2>

          <p style={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: '1.6', marginBottom: '1rem' }}>
            {selectedThread.content}
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <button
              onClick={(e) => handleLikeThread(selectedThread.id, e)}
              style={{
                background: 'rgba(0, 255, 255, 0.1)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                color: '#00ffff',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <ThumbsUp size={16} />
              {formatNumber(selectedThread.likes_count || 0)}
            </button>
            <button
              onClick={(e) => handleLoveThread(selectedThread.id, e)}
              style={{
                background: 'rgba(255, 0, 255, 0.1)',
                border: '1px solid rgba(255, 0, 255, 0.3)',
                color: '#ff00ff',
                padding: '0.5rem 1rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Heart size={16} />
              {formatNumber(selectedThread.likes_count || 0)}
            </button>
          </div>
        </div>

        {/* Comment input - shown in post view mode */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#fff', marginBottom: '0.75rem' }}>
            Add a Comment
          </h3>
          <textarea
            data-testid="comment-input"
            value={commentContent}
            onChange={(e) => setCommentContent(e.target.value)}
            placeholder="Add a comment..."
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '0.75rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              outline: 'none',
              resize: 'vertical'
            }}
          />
          <button
            data-testid="post-comment-btn"
            onClick={postComment}
            style={{
              marginTop: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.2), rgba(0, 255, 255, 0.2))',
              border: '1px solid rgba(255, 0, 255, 0.5)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Post Comment
          </button>
        </div>
        </>
        )}

        {detailViewMode === 'comments' && (
        <>
        {/* Comments section */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fff', marginBottom: '1rem' }}>
            Comments ({threadComments.length})
          </h3>

          {/* Comments list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {threadComments.map(comment => (
              <div
                key={comment.id}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  borderRadius: '0.5rem',
                  padding: '1rem'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#00ffff', fontSize: '0.875rem', fontWeight: '600' }}>
                    {comment.author_name}
                  </span>
                  <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
                    {getTimeAgo(comment.created_at)}
                  </span>
                </div>

                {editingCommentId === comment.id ? (
                  <div>
                    <textarea
                      value={editCommentContent}
                      onChange={(e) => setEditCommentContent(e.target.value)}
                      rows={2}
                      style={{ width: '100%', padding: '0.5rem', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,255,0.4)', borderRadius: '0.4rem', color: '#fff', fontSize: '0.85rem', outline: 'none', resize: 'vertical' }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem' }}>
                      <button onClick={() => handleEditComment(comment.id)} style={{ background: 'rgba(0,255,0,0.2)', border: '1px solid rgba(0,255,0,0.5)', borderRadius: '0.4rem', color: '#00ff00', padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><Check size={11} /> Save</button>
                      <button onClick={() => { setEditingCommentId(null); setEditCommentContent(''); }} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '0.4rem', color: 'rgba(255,255,255,0.7)', padding: '0.2rem 0.6rem', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.2rem' }}><X size={11} /> Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                    {comment.content}
                  </p>
                )}

                {/* Reactions display */}
                {comment.reactions && comment.reactions.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.3rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    {comment.reactions.map((r, rIdx) => (
                      <span key={rIdx} onClick={() => handleCommentReaction(comment.id, r.emoji)} style={{ background: 'rgba(255,0,255,0.1)', border: '1px solid rgba(255,0,255,0.25)', borderRadius: '1rem', padding: '0.1rem 0.4rem', fontSize: '0.7rem', cursor: 'pointer' }}>{r.emoji} {r.count}</span>
                    ))}
                  </div>
                )}

                {/* Comment actions row */}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                  {/* Reaction picker */}
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setReactionPickerCommentId(reactionPickerCommentId === comment.id ? null : comment.id)}
                      style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '0.15rem 0.35rem', borderRadius: '0.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#ff00ff'; e.currentTarget.style.borderColor = 'rgba(255,0,255,0.4)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                    ><Smile size={12} /></button>
                    {reactionPickerCommentId === comment.id && (
                      <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: '0.2rem', background: 'rgba(20,20,35,0.98)', border: '1px solid rgba(255,0,255,0.4)', borderRadius: '0.5rem', padding: '0.3rem', display: 'flex', gap: '0.2rem', zIndex: 100, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
                        {REACTION_EMOJIS.map(emoji => (
                          <button key={emoji} onClick={() => handleCommentReaction(comment.id, emoji)} style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer', padding: '0.15rem', borderRadius: '4px', transition: 'transform 0.15s' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>{emoji}</button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Author-only edit/delete */}
                  {(() => {
                    const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                    const isAuthor = currentUser.id === comment.author_id || currentUser.user_id === comment.author_id;
                    return isAuthor ? (
                      <>
                        <button
                          onClick={() => { setEditingCommentId(comment.id); setEditCommentContent(comment.content); }}
                          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '0.15rem 0.35rem', borderRadius: '0.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#00ffff'; e.currentTarget.style.borderColor = 'rgba(0,255,255,0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                        ><Pencil size={12} /></button>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '0.15rem 0.35rem', borderRadius: '0.3rem', cursor: 'pointer', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ff4444'; e.currentTarget.style.borderColor = 'rgba(255,0,0,0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                        ><Trash2 size={12} /></button>
                      </>
                    ) : null;
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </div>
    );
  }

  // Thread List View
  return (
    <div className="forum-tab" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', maxHeight: '75vh' }}>
      {/* Toolbar */}
      <div className="forum-toolbar" style={{
        padding: '1rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        gap: '0.75rem',
        alignItems: 'center',
        flexWrap: 'wrap',
        background: 'rgba(0, 0, 0, 0.2)',
        flexShrink: 0
      }}>
        {/* New Thread button */}
        <button
          onClick={() => setShowCreateThread(true)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#00ffff',
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            padding: '0.5rem 0',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#ff00ff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#00ffff';
          }}
        >
          + new thread
        </button>

        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '1rem',
            height: '1rem',
            color: 'rgba(255, 255, 255, 0.5)'
          }} />
          <input
            type="text"
            placeholder="Search threads..."
            value={forumSearchTerm}
            onChange={(e) => setForumSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0.5rem',
              color: '#fff',
              fontSize: '0.875rem',
              outline: 'none'
            }}
          />
        </div>

        {/* Forum Category Filter (Rebuilt - Isolated from News) */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{
            padding: '0.75rem 2rem 0.75rem 1rem',
            background: 'rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '0.5rem',
            color: '#fff',
            fontSize: '0.875rem',
            cursor: 'pointer',
            outline: 'none'
          }}
        >
          <option value="all" style={{ background: '#1a1a2e' }}>All Categories</option>
          {forumCategories && forumCategories.length > 0 && forumCategories.map(cat => (
            <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e' }}>
              {cat.name} ({cat.count || 0})
            </option>
          ))}
        </select>
      </div>

      {/* Threads list */}
      <div className="forum-threads" style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {loadingForum ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading threads...</p>
          </div>
        ) : forumThreads.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {forumThreads.filter(thread => 
              !forumSearchTerm || 
              thread.title.toLowerCase().includes(forumSearchTerm.toLowerCase()) ||
              thread.content.toLowerCase().includes(forumSearchTerm.toLowerCase())
            ).map(thread => (
              <div
                key={thread.id}
                onClick={() => {setSelectedThread(thread); loadThreadComments(thread.id);}}
                style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.75rem',
                  padding: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.8)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.5)';
                  e.currentTarget.style.transform = 'scale(1.02) translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 0, 255, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.3)';
                  e.currentTarget.style.transform = 'scale(1) translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Header row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                    <div style={{
                      background: 'rgba(255, 0, 255, 0.15)',
                      color: '#ff00ff',
                      fontSize: '0.7rem',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '0.75rem',
                      fontWeight: '600',
                      border: '1px solid rgba(255, 0, 255, 0.3)'
                    }}>
                      {forumCategories.find(c => c.id === thread.category)?.name || thread.category}
                    </div>
                    <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem' }}>
                      by {thread.author_name} • {getTimeAgo(thread.created_at)}
                    </span>
                  </div>
                </div>

                {/* Title & Content - Inline Edit or Display */}
                {editingThreadId === thread.id ? (
                  <div onClick={(e) => e.stopPropagation()} style={{ marginBottom: '0.75rem' }}>
                    <input
                      data-testid={`edit-title-${thread.id}`}
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        marginBottom: '0.5rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(0, 255, 255, 0.4)',
                        borderRadius: '0.4rem',
                        color: '#fff',
                        fontSize: '1rem',
                        fontWeight: '600',
                        outline: 'none'
                      }}
                    />
                    <textarea
                      data-testid={`edit-content-${thread.id}`}
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        background: 'rgba(0, 0, 0, 0.4)',
                        border: '1px solid rgba(0, 255, 255, 0.4)',
                        borderRadius: '0.4rem',
                        color: '#fff',
                        fontSize: '0.85rem',
                        outline: 'none',
                        resize: 'vertical'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button
                        data-testid={`save-edit-${thread.id}`}
                        onClick={(e) => handleEditThread(thread.id, e)}
                        style={{
                          background: 'rgba(0, 255, 0, 0.2)',
                          border: '1px solid rgba(0, 255, 0, 0.5)',
                          borderRadius: '0.4rem',
                          color: '#00ff00',
                          padding: '0.3rem 0.75rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <Check size={12} /> Save
                      </button>
                      <button
                        data-testid={`cancel-edit-${thread.id}`}
                        onClick={(e) => cancelEditing(e)}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: '0.4rem',
                          color: 'rgba(255, 255, 255, 0.7)',
                          padding: '0.3rem 0.75rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <X size={12} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <h4 style={{
                    fontWeight: '600',
                    color: '#fff',
                    fontSize: '1rem',
                    marginBottom: '0.75rem'
                  }}>
                    {thread.title}
                  </h4>
                )}

                {/* Existing Reactions Display */}
                {thread.reactions && thread.reactions.length > 0 && (
                  <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    {thread.reactions.map((reaction, rIdx) => (
                      <span
                        key={rIdx}
                        onClick={(e) => handleReaction(thread.id, reaction.emoji, e)}
                        style={{
                          background: 'rgba(255, 0, 255, 0.1)',
                          border: '1px solid rgba(255, 0, 255, 0.25)',
                          borderRadius: '1rem',
                          padding: '0.15rem 0.5rem',
                          fontSize: '0.75rem',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {reaction.emoji} {reaction.count}
                      </span>
                    ))}
                  </div>
                )}

                {/* Stats & Actions */}
                <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.75rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Reactions Button */}
                  <div style={{ position: 'relative' }}>
                    <button
                      data-testid={`reaction-btn-${thread.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setReactionPickerThreadId(reactionPickerThreadId === thread.id ? null : thread.id);
                      }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: 'rgba(255, 255, 255, 0.6)',
                        padding: '0.3rem 0.5rem',
                        borderRadius: '0.4rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = '#ff00ff'; e.currentTarget.style.borderColor = 'rgba(255, 0, 255, 0.4)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; }}
                    >
                      <Smile size={14} />
                    </button>
                    {reactionPickerThreadId === thread.id && (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: 0,
                          marginBottom: '0.25rem',
                          background: 'rgba(20, 20, 35, 0.98)',
                          border: '1px solid rgba(255, 0, 255, 0.4)',
                          borderRadius: '0.5rem',
                          padding: '0.4rem',
                          display: 'flex',
                          gap: '0.25rem',
                          zIndex: 100,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.5)'
                        }}
                      >
                        {REACTION_EMOJIS.map(emoji => (
                          <button
                            key={emoji}
                            onClick={(e) => handleReaction(thread.id, emoji, e)}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '1.1rem',
                              cursor: 'pointer',
                              padding: '0.2rem',
                              borderRadius: '4px',
                              transition: 'transform 0.15s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Like Button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLikeThread(thread.id, e); }}
                    style={{
                      background: 'rgba(0, 255, 255, 0.1)',
                      border: '1px solid rgba(0, 255, 255, 0.3)',
                      color: '#00ffff',
                      padding: '0.3rem 0.5rem',
                      borderRadius: '0.4rem',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    <ThumbsUp size={12} /> {formatNumber(thread.likes_count || 0)}
                  </button>

                  {/* Comments Button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedThread(thread); loadThreadComments(thread.id); }}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'rgba(255, 255, 255, 0.8)',
                      padding: '0.3rem 0.5rem',
                      borderRadius: '0.4rem',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    <MessageCircle size={12} /> {thread.replies_count || 0}
                  </button>

                  {/* Author-only: Edit & Delete */}
                  {(() => {
                    const currentUser = JSON.parse(localStorage.getItem('beatseek_user') || '{}');
                    const isAuthor = currentUser.id === thread.author_id || currentUser.user_id === thread.author_id;
                    return isAuthor ? (
                      <>
                        <button
                          data-testid={`edit-btn-${thread.id}`}
                          onClick={(e) => startEditing(thread, e)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'rgba(255, 255, 255, 0.6)',
                            padding: '0.3rem 0.5rem',
                            borderRadius: '0.4rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#00ffff'; e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; }}
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          data-testid={`delete-btn-${thread.id}`}
                          onClick={(e) => handleDeleteThread(thread.id, e)}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'rgba(255, 255, 255, 0.6)',
                            padding: '0.3rem 0.5rem',
                            borderRadius: '0.4rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#ff4444'; e.currentTarget.style.borderColor = 'rgba(255, 0, 0, 0.4)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)'; e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; }}
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    ) : null;
                  })()}
                  </div>

                  {/* View Comments & View Post buttons - bottom right */}
                  <button
                    data-testid={`view-comments-${thread.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailViewMode('comments');
                      setSelectedThread(thread);
                      loadThreadComments(thread.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(0, 255, 255, 0.7)',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '0.3rem 0',
                      transition: 'color 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#00ffff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(0, 255, 255, 0.7)'}
                  >
                    View Comments
                  </button>
                  <button
                    data-testid={`view-post-${thread.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailViewMode('post');
                      setSelectedThread(thread);
                      loadThreadComments(thread.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255, 0, 255, 0.7)',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      padding: '0.3rem 0',
                      transition: 'color 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ff00ff'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 0, 255, 0.7)'}
                  >
                    View Post →
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', textAlign: 'center' }}>
            <MessageCircle size={48} style={{ color: 'rgba(255, 255, 255, 0.3)', marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fff', marginBottom: '0.5rem' }}>
              No Threads Yet
            </h3>
            <p style={{ color: 'rgba(255, 255, 255, 0.6)', maxWidth: '28rem' }}>
              Be the first to start a conversation!
            </p>
          </div>
        )}
      </div>

      {/* Create Thread Modal */}
      {showCreateThread && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            border: '2px solid rgba(255, 0, 255, 0.3)',
            borderRadius: '1rem',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(255, 0, 255, 0.3)'
          }}>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fff', marginBottom: '1.5rem' }}>
              Create New Thread
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <select
                value={newThreadCategory}
                onChange={(e) => setNewThreadCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.5rem',
                  color: '#fff',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value="" style={{ background: '#1a1a2e' }}>Select a category...</option>
                {forumCategories && forumCategories.length > 0 && forumCategories.map(cat => (
                  <option key={cat.id} value={cat.id} style={{ background: '#1a1a2e' }}>
                    {cat.name}
                  </option>
                ))}
              </select>

              <input
                type="text"
                placeholder="Thread title..."
                value={newThreadTitle}
                onChange={(e) => setNewThreadTitle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.5rem',
                  color: '#fff',
                  fontSize: '0.875rem',
                  outline: 'none'
                }}
              />

              <textarea
                placeholder="Thread content..."
                value={newThreadContent}
                onChange={(e) => setNewThreadContent(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '150px',
                  padding: '0.75rem',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.5rem',
                  color: '#fff',
                  fontSize: '0.875rem',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowCreateThread(false);
                    setNewThreadTitle('');
                    setNewThreadContent('');
                    setNewThreadCategory('');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '0.5rem',
                    color: '#fff',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={createThread}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'linear-gradient(135deg, rgba(255, 0, 255, 0.3), rgba(0, 255, 255, 0.3))',
                    border: '1px solid rgba(255, 0, 255, 0.5)',
                    borderRadius: '0.5rem',
                    color: '#fff',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Create Thread
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForumTab;
